import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, CalendarDays, MapPin, Users, ArrowRight, Search } from "lucide-react";
import { api, startGoogleLogin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePreview } from "@/context/PreviewContext";
import { StatusPill, FadeIn } from "@/components/common";
import {
  ProducerShell, ProducerNav, PSectionLabel, PSpinner, useProducerTheme, portalSurface,
} from "@/components/producer";
import { LocationField } from "@/components/LocationField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "Europe/London", "Europe/Berlin", "Europe/Paris", "Asia/Dubai", "Asia/Kolkata",
  "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney",
];

function Gate({ title, body, tone = "amber" }) {
  const toneCls = tone === "red"
    ? "text-red-600 bg-red-500/10 border-red-500/30"
    : "text-amber-600 bg-amber-500/10 border-amber-500/30";
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6">
      <FadeIn className="p-card max-w-md p-8 text-center">
        <div className={`mx-auto mb-5 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-widest ${toneCls}`}>
          Access gate
        </div>
        <h2 className="font-display text-2xl font-bold">{title}</h2>
        <p className="mt-3 text-sm p-muted">{body}</p>
      </FadeIn>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="mb-1.5 block text-xs p-muted">{label}</Label>
      {children}
    </div>
  );
}

function Onboarding({ onDone }) {
  const [form, setForm] = useState({
    business_name: "", contact_name: "", phone: "", website: "",
    billing_name: "", billing_card_last4: "", terms_agreed: false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async () => {
    if (!form.business_name || !form.contact_name || !form.billing_name) {
      toast.error("Please fill business, contact and billing name"); return;
    }
    if (!form.terms_agreed) { toast.error("You must agree to the commercial terms"); return; }
    setSaving(true);
    try {
      await api.post("/producer/onboarding", form);
      toast.success("Onboarding complete — you're active!");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Onboarding failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <FadeIn>
        <PSectionLabel>Producer onboarding</PSectionLabel>
        <h1 className="font-display mt-2 text-3xl font-bold">Let's get you set up</h1>
        <p className="mt-2 text-sm p-muted">A few details, agree to terms, and you can start creating events.</p>
      </FadeIn>

      <FadeIn delay={0.1}>
        <div className="p-card mt-8 space-y-6 p-7">
          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest p-accent-text">Business</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Business name" testId="ob-business"><Input data-testid="ob-business-input" value={form.business_name} onChange={set("business_name")} placeholder="Velvet Room Events" /></Field>
              <Field label="Contact name" testId="ob-contact"><Input data-testid="ob-contact-input" value={form.contact_name} onChange={set("contact_name")} placeholder="Your name" /></Field>
              <Field label="Phone (optional)"><Input value={form.phone} onChange={set("phone")} placeholder="+1…" /></Field>
              <Field label="Website (optional)"><Input value={form.website} onChange={set("website")} placeholder="https://" /></Field>
            </div>
          </div>

          <div className="p-inset p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-600">Commercial terms</p>
            <p className="mt-2 text-sm p-muted">You agree to pay <span className="font-bold p-accent-text">$10 per surveyed guest</span>, locked at T-1 (one hour before the event) and charged seven days after.</p>
            <label className="mt-4 flex cursor-pointer items-center gap-3">
              <Checkbox data-testid="ob-terms-checkbox" checked={form.terms_agreed} onCheckedChange={(v) => setForm((f) => ({ ...f, terms_agreed: !!v }))} />
              <span className="text-sm p-muted">I agree to the Goose commercial terms</span>
            </label>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest p-accent-text">Billing setup</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Billing name" testId="ob-billing"><Input data-testid="ob-billing-input" value={form.billing_name} onChange={set("billing_name")} placeholder="Legal entity" /></Field>
              <Field label="Card last 4 (simulated)"><Input value={form.billing_card_last4} onChange={set("billing_card_last4")} maxLength={4} placeholder="4242" /></Field>
            </div>
            <p className="mt-2 text-xs p-subtle">Billing is simulated in V1 — no real card is collected.</p>
          </div>

          <Button data-testid="ob-submit-btn" disabled={saving} onClick={submit} className="p-btn w-full py-6 text-base">
            {saving ? "Saving…" : "Complete onboarding"}
          </Button>
        </div>
      </FadeIn>
    </div>
  );
}

function CreateEventDialog() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { theme } = useProducerTheme();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", date: "", start_time: "", timezone: "America/New_York", location: "", location_id: null, location_details: null });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async () => {
    if (!form.name || !form.date || !form.start_time || !(form.location || form.location_id)) { toast.error("Fill all fields, including a venue"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/producer/events", {
        name: form.name, date: form.date, start_time: form.start_time, timezone: form.timezone,
        location: form.location, location_id: form.location_id, location_details: form.location_details,
      });
      toast.success("Event created — let's add Goose to it");
      qc.invalidateQueries({ queryKey: ["producer-events"] });
      qc.invalidateQueries({ queryKey: ["producer-locations"] });
      setOpen(false);
      navigate(`/producer/events/${data.event_id}/setup`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create event");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-event-btn" className="p-btn px-5">
          <Plus className="mr-1.5 h-4 w-4" /> New event
        </Button>
      </DialogTrigger>
      <DialogContent className={`goose-producer ${theme === "dark" ? "theme-dark" : ""} ${portalSurface(theme)}`}>
        <DialogHeader><DialogTitle className="font-display text-xl">Create an event</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Enter your event name, date, time, time zone and venue to create a new Goose event.</DialogDescription>
        <div className="space-y-4 py-2">
          <Field label="Event name"><Input data-testid="ce-name-input" value={form.name} onChange={set("name")} placeholder="Neon Social — Friday" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date"><Input data-testid="ce-date-input" type="date" value={form.date} onChange={set("date")} /></Field>
            <Field label="Start time"><Input data-testid="ce-time-input" type="time" value={form.start_time} onChange={set("start_time")} /></Field>
          </div>
          <Field label="Time zone">
            <Select value={form.timezone} onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))}>
              <SelectTrigger data-testid="ce-tz-trigger"><SelectValue /></SelectTrigger>
              <SelectContent className={portalSurface(theme)}>
                {TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Location">
            <LocationField
              theme={theme}
              value={{ location: form.location, location_id: form.location_id, location_details: form.location_details }}
              onChange={(v) => setForm((f) => ({ ...f, location: v.location, location_id: v.location_id, location_details: v.location_details }))}
            />
          </Field>
        </div>
        <DialogFooter>
          <Button data-testid="ce-submit-btn" disabled={saving} onClick={submit} className="p-btn w-full">
            {saving ? "Creating…" : "Create event"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FILTERS = [
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
  { key: "all", label: "All" },
];

function EventRow({ ev, navigate }) {
  const city = ev.location_details?.city;
  return (
    <tr
      data-testid={`event-row-${ev.slug}`}
      className="group cursor-pointer border-t p-border-c transition-colors hover:bg-[color:var(--p-card-2)]"
      onClick={() => navigate(`/producer/events/${ev.event_id}`)}
    >
      <td className="px-4 py-4">
        <p className="font-semibold">{ev.name}</p>
        <p className="font-mono-g text-xs p-subtle">{ev.goose_id}</p>
      </td>
      <td className="px-4 py-4 text-sm p-muted whitespace-nowrap">{ev.date} · {ev.start_time}</td>
      <td className="px-4 py-4 text-sm p-muted">
        <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 p-subtle" /> {ev.location}</span>
        {city && <span className="ml-5 text-xs p-subtle">{city}</span>}
      </td>
      <td className="px-4 py-4 text-sm p-muted whitespace-nowrap">{ev.surveyed} surveyed · {ev.guest_count} attributed</td>
      <td className="px-4 py-4"><StatusPill status={ev.status} /></td>
      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-2 whitespace-nowrap">
          <button
            data-testid={`open-dashboard-${ev.slug}`}
            onClick={() => navigate(`/producer/events/${ev.event_id}`)}
            className="flex items-center gap-1.5 text-sm font-semibold p-accent-text"
          >
            Open <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            data-testid={`setup-${ev.slug}`}
            onClick={() => navigate(`/producer/events/${ev.event_id}/setup`)}
            className={ev.setup_complete
              ? "p-btn-ghost px-3 py-1.5 text-xs"
              : "rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-600 hover:bg-amber-500/20"}
          >
            {ev.setup_complete ? "Setup" : "Finish setup"}
          </button>
        </div>
      </td>
    </tr>
  );
}

function EventCard({ ev, navigate, delay }) {
  const city = ev.location_details?.city;
  return (
    <FadeIn delay={delay}>
      <motion.div
        data-testid={`event-card-${ev.slug}`}
        className="p-card p-card-hover group w-full cursor-pointer p-6 text-left"
      >
        <div onClick={() => navigate(`/producer/events/${ev.event_id}`)}>
          <div className="flex items-start justify-between">
            <StatusPill status={ev.status} />
            <span className="font-mono-g text-xs p-subtle">{ev.goose_id}</span>
          </div>
          <h3 className="font-display mt-4 text-xl font-bold">{ev.name}</h3>
          <div className="mt-3 space-y-1.5 text-sm p-muted">
            <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 p-subtle" /> {ev.date} · {ev.start_time}</p>
            <p className="flex items-center gap-2"><MapPin className="h-4 w-4 p-subtle" /> {ev.location}{city ? ` · ${city}` : ""}</p>
            <p className="flex items-center gap-2"><Users className="h-4 w-4 p-subtle" /> {ev.surveyed} surveyed · {ev.guest_count} attributed</p>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-between">
          <button
            data-testid={`open-dashboard-mobile-${ev.slug}`}
            onClick={() => navigate(`/producer/events/${ev.event_id}`)}
            className="flex items-center gap-1.5 text-sm font-semibold p-accent-text"
          >
            Open dashboard <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            data-testid={`setup-mobile-${ev.slug}`}
            onClick={(e) => { e.stopPropagation(); navigate(`/producer/events/${ev.event_id}/setup`); }}
            className={ev.setup_complete
              ? "p-btn-ghost px-3 py-1.5 text-xs"
              : "rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-600 hover:bg-amber-500/20"}
          >
            {ev.setup_complete ? "Event setup" : "Finish setup"}
          </button>
        </div>
      </motion.div>
    </FadeIn>
  );
}

function EventsList() {
  const navigate = useNavigate();
  const { preview } = usePreview();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("upcoming");
  const { data, isLoading } = useQuery({
    queryKey: ["producer-events"],
    queryFn: async () => (await api.get("/producer/events")).data,
  });

  const events = useMemo(() => data?.events || [], [data]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const q = query.trim().toLowerCase();
    return events.filter((ev) => {
      const startMs = ev.start_at ? new Date(ev.start_at).getTime() : 0;
      if (filter === "upcoming" && startMs < now) return false;
      if (filter === "past" && startMs >= now) return false;
      if (!q) return true;
      const city = (ev.location_details?.city || "").toLowerCase();
      return ev.name.toLowerCase().includes(q)
        || (ev.location || "").toLowerCase().includes(q)
        || city.includes(q);
    });
  }, [events, query, filter]);

  if (isLoading) return <PSpinner />;

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <FadeIn>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <PSectionLabel>Your events</PSectionLabel>
            <h1 className="font-display mt-2 text-3xl font-bold sm:text-4xl">Events</h1>
          </div>
          {!preview && <CreateEventDialog />}
        </div>
      </FadeIn>

      {events.length === 0 ? (
        <FadeIn delay={0.1}>
          <div className="p-card mt-10 flex flex-col items-center p-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl p-accent-text" style={{ background: "var(--p-accent-soft)" }}><CalendarDays className="h-6 w-6" /></div>
            <h3 className="font-display mt-5 text-xl font-semibold">No events yet</h3>
            <p className="mt-2 max-w-sm text-sm p-muted">Create your first event to generate an attribution link, QR code and live dashboard.</p>
          </div>
        </FadeIn>
      ) : (
        <>
          <FadeIn delay={0.05}>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative sm:max-w-xs sm:flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 p-subtle" />
                <Input data-testid="events-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by event or city…" className="pl-9" />
              </div>
              <div className="inline-flex rounded-full p-inset p-1">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    data-testid={`filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${filter === f.key ? "text-white" : "p-muted"}`}
                    style={filter === f.key ? { background: "var(--p-accent)" } : undefined}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </FadeIn>

          {filtered.length === 0 ? (
            <FadeIn delay={0.08}>
              <div className="p-card mt-6 flex flex-col items-center p-14 text-center" data-testid="events-empty">
                <Search className="h-6 w-6 p-subtle" />
                <h3 className="font-display mt-4 text-lg font-semibold">No matching events</h3>
                <p className="mt-1 text-sm p-muted">Try a different search or filter.</p>
              </div>
            </FadeIn>
          ) : (
            <>
              {/* Desktop table */}
              <FadeIn delay={0.08}>
                <div className="mt-6 hidden overflow-hidden p-card md:block">
                  <table className="w-full text-left" data-testid="events-table">
                    <thead>
                      <tr className="text-xs font-semibold uppercase tracking-wider p-subtle">
                        <th className="px-4 py-3">Event</th>
                        <th className="px-4 py-3">When</th>
                        <th className="px-4 py-3">Location</th>
                        <th className="px-4 py-3">Guests</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((ev) => <EventRow key={ev.event_id} ev={ev} navigate={navigate} />)}
                    </tbody>
                  </table>
                </div>
              </FadeIn>

              {/* Mobile cards */}
              <div className="mt-6 grid grid-cols-1 gap-5 md:hidden">
                {filtered.map((ev, i) => <EventCard key={ev.event_id} ev={ev} navigate={navigate} delay={0.05 * i} />)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default function ProducerHome() {
  const { user, loading, refresh } = useAuth();
  const { preview } = usePreview();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const onOnboarded = () => {
    refresh();
    qc.invalidateQueries({ queryKey: ["producer-status"] });
  };

  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ["producer-status", preview?.producer_id],
    queryFn: async () => (await api.get("/producer/status")).data,
    enabled: !!preview,
  });

  if (loading) return <ProducerShell><PSpinner /></ProducerShell>;

  if (!user) {
    return (
      <ProducerShell>
        <div className="flex min-h-screen items-center justify-center px-6">
          <FadeIn className="p-card max-w-md p-8 text-center">
            <p className="font-display text-3xl font-extrabold tracking-tight p-accent-text">GOOSE<span className="p-muted">.</span></p>
            <h2 className="font-display mt-5 text-2xl font-bold">Sign in</h2>
            <p className="mt-2 text-sm p-muted">Sign in with Google to access your Goose events.</p>
            <Button data-testid="signin-google-btn" onClick={() => startGoogleLogin()} className="p-btn mt-6 w-full py-6">
              Sign in with Google
            </Button>
            <button data-testid="admin-signin-link" onClick={() => navigate("/admin")}
              className="mt-4 inline-flex items-center gap-1 text-xs p-subtle transition-colors hover:opacity-80">
              HAEVN admin? Sign in here <span aria-hidden="true">&rarr;</span>
            </button>
          </FadeIn>
        </div>
      </ProducerShell>
    );
  }

  if (user.role === "operator" && !preview) {
    return (
      <ProducerShell>
        <div className="flex min-h-screen items-center justify-center px-6">
          <FadeIn className="p-card max-w-md p-8 text-center">
            <p className="font-display text-3xl font-extrabold tracking-tight p-accent-text">GOOSE<span className="p-muted">.</span></p>
            <p className="mt-4 inline-flex rounded-full border p-border-c px-3 py-1 text-xs font-semibold uppercase tracking-widest p-muted">Signed in as HAEVN Admin</p>
            <h2 className="font-display mt-4 text-2xl font-bold">This is the Producer app</h2>
            <p className="mt-2 text-sm p-muted">Your account is a HAEVN Admin, not a producer. Admin tools live at the separate /admin sign-in.</p>
            <Button data-testid="go-to-admin-btn" onClick={() => navigate("/admin")} className="p-btn mt-6 w-full py-6">
              Go to Admin console
            </Button>
            {user.preview_mode_enabled && (
              <p className="mt-4 text-xs text-amber-600">Test Mode: to view a producer's experience, open Admin &rarr; Producers &rarr; "Preview as producer".</p>
            )}
          </FadeIn>
        </div>
      </ProducerShell>
    );
  }

  if (preview && statusLoading) return <ProducerShell><PSpinner /></ProducerShell>;

  const prod = preview ? statusData?.producer : user.producer;

  return (
    <ProducerShell>
      <ProducerNav active="events" />
      {!prod ? (
        <Gate title="Awaiting approval" body="This account isn't approved yet. A HAEVN operator must approve the business before it can onboard and run events." />
      ) : prod.status === "rejected" ? (
        <Gate tone="red" title="Application declined" body="This producer application was not approved. Contact the HAEVN team for details." />
      ) : prod.status === "invited" || prod.status === "onboarding" ? (
        preview ? (
          <Gate title="Onboarding in progress" body="This producer hasn't finished onboarding yet, so there's no dashboard to preview. You're viewing as an Admin in read-only mode." />
        ) : (
          <Onboarding onDone={onOnboarded} />
        )
      ) : (
        <EventsList />
      )}
    </ProducerShell>
  );
}
